import { useState, useEffect, useRef } from "react";
import { CriteriaFilter } from "../types";
import { useClickOutside } from "../../../hooks/useClickOutside";

export interface UseCriteriaFilterProps {
  criteriaFilter?: CriteriaFilter;
  onCriteriaFilterChange?: (filter?: CriteriaFilter) => void;
  onLoadCriteria?: () => Promise<void>;
  maxCriteriaDisplay?: number;
}

/**
 * Custom hook to manage criteria filter state and interactions.
 */
export function useCriteriaFilter({
  criteriaFilter,
  onCriteriaFilterChange,
  onLoadCriteria,
  maxCriteriaDisplay = 20,
}: UseCriteriaFilterProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize selectedValues from existing criteriaFilter
  useEffect(() => {
    if (criteriaFilter) {
      setSelectedValues(new Set(criteriaFilter.values));
    } else {
      setSelectedValues(new Set());
    }
  }, [criteriaFilter]);

  // Re-load criteria against the current filters whenever the dropdown opens.
  // loadCriteria's own validity guard makes this instant when nothing changed.
  useEffect(() => {
    if (isExpanded && onLoadCriteria) {
      onLoadCriteria();
    }
  }, [isExpanded, onLoadCriteria]);

  // Close criteria dropdown when clicking outside
  useClickOutside(containerRef, () => {
    if (isExpanded) {
      setIsExpanded(false);
      // Reset selected values to match current filter
      if (criteriaFilter) {
        setSelectedValues(new Set(criteriaFilter.values));
      } else {
        setSelectedValues(new Set());
      }
    }
  }, isExpanded);

  const handleValueSelect = (value: string) => {
    const newSet = new Set(selectedValues);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      // Prevent selecting more than maxCriteriaDisplay criteria
      if (newSet.size >= maxCriteriaDisplay) {
        return; // Don't add if limit reached
      }
      newSet.add(value);
    }
    setSelectedValues(newSet);
  };

  const handleApply = () => {
    const values = Array.from(selectedValues);
    if (values.length > 0) {
      onCriteriaFilterChange?.({ values });
    } else {
      onCriteriaFilterChange?.(undefined);
    }
    setIsExpanded(false);
  };

  const handleCancel = () => {
    setIsExpanded(false);
    // Reset selected values to match current filter
    if (criteriaFilter) {
      setSelectedValues(new Set(criteriaFilter.values));
    } else {
      setSelectedValues(new Set());
    }
  };

  const handleRemove = () => {
    onCriteriaFilterChange?.(undefined);
    setSelectedValues(new Set());
  };

  return {
    isExpanded,
    setIsExpanded,
    selectedValues,
    containerRef,
    handleValueSelect,
    handleApply,
    handleCancel,
    handleRemove,
    isActive: !!criteriaFilter,
    canApply: selectedValues.size > 0,
  };
}
